/**
 * ModuleScaffold — scaffold concreto para módulos del roadmap que aún
 * no tienen backend de datos pero sí un propósito y entregables claros.
 *
 * No es un "coming soon" vacío: declara qué resuelve, qué endpoints
 * consumirá y qué se podrá hacer, para que la navegación completa de
 * la nueva estructura quede operativa y trazable.
 */
import { Link } from "react-router-dom";

import { Icon, type IconName } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";

interface ModuleScaffoldProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  icon: IconName;
  proposito: string;
  entregables: string[];
  endpoints: string[];
  relacionado?: { to: string; label: string }[];
}

export function ModuleScaffold({
  eyebrow,
  title,
  subtitle,
  icon,
  proposito,
  entregables,
  endpoints,
  relacionado = [],
}: ModuleScaffoldProps): JSX.Element {
  return (
    <>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <section className="card">
          <div className="card__title">
            <span className="card__title-row">
              <Icon name={icon} size={15} /> Propósito operacional
            </span>
            <span className="badge badge--primary">Roadmap</span>
          </div>
          <p style={{ color: "var(--c-text)", fontSize: 13.5 }}>{proposito}</p>

          <div className="card__title" style={{ marginTop: 14 }}>
            Qué entregará
          </div>
          <div className="vstack" style={{ gap: 6 }}>
            {entregables.map((e) => (
              <div key={e} className="hstack" style={{ alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: "var(--c-success)", marginTop: 2 }}>
                  <Icon name="shield" size={13} />
                </span>
                <span style={{ fontSize: 13 }}>{e}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <div className="card__title">Endpoints previstos</div>
          <div className="vstack" style={{ gap: 4 }}>
            {endpoints.map((ep) => (
              <code key={ep} style={{ display: "block", padding: "4px 8px" }}>
                {ep}
              </code>
            ))}
          </div>

          {relacionado.length > 0 && (
            <>
              <div className="card__title" style={{ marginTop: 14 }}>
                Módulos relacionados
              </div>
              <div className="hstack" style={{ flexWrap: "wrap", gap: 8 }}>
                {relacionado.map((r) => (
                  <Link key={r.to} to={r.to} className="btn btn--ghost">
                    {r.label} <Icon name="chevron-right" size={12} />
                  </Link>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      <div className="status-block status-block--info">
        <Icon name="clock" size={14} />
        Este módulo está cableado en la navegación y listo para conectar a su
        fuente de datos. La estructura, RBAC y rutas ya están activas.
      </div>
    </>
  );
}
